<template>
  <div class="details-container">
    <div v-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Retrieving pay stub details...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="$router.push({ name: 'MyPayStubs' })" class="back-link">
        &larr; Back to List
      </button>
    </div>

    <div v-else-if="stub" class="stub-wrapper">
      <!-- Toolbar -->
      <div class="stub-toolbar no-print">
        <router-link :to="{ name: 'MyPayStubs' }" class="back-btn">
          &larr; Back
        </router-link>
        <div class="toolbar-actions">
          <button @click="printStub" class="print-btn print-btn--secondary">
            Print
          </button>
          <button @click="downloadPdf" class="print-btn" :disabled="downloading">
            {{ downloading ? 'Preparing PDF…' : 'Download PDF' }}
          </button>
        </div>
      </div>

      <!-- On Screen View -->
      <div class="paper-sheet">
        <div class="print-header">
          <div class="company-info">
            <h1>{{ invoiceData?.companyName || 'RTH Cleaners Agency LLC' }}</h1>
            <p>{{ invoiceData?.companyAddress || '123 Cleaning Way, City, State, Zip' }}</p>
            <p>Phone: (865) 352-8700</p>
          </div>
          <div class="pay-stub-title">
            <h2>PAY STUB</h2>
            <p>Pay Date: {{ formatDate(stub.payDate) }}</p>
          </div>
        </div>

        <div class="info-row">
          <div class="info-col">
            <h3 class="section-label">EMPLOYEE</h3>
            <div class="info-content">
              <p class="emp-name">{{ stub.employeeName }}</p>
              <p>ID: {{ stub.employeeNumber }}</p>
              <p v-if="employeeAddress" class="address-lines">
                Address: {{ employeeAddress }}
              </p>
            </div>
          </div>
          <div class="info-col">
            <h3 class="section-label">PAY PERIOD</h3>
            <div class="info-content">
              <p>{{ formatDate(stub.payPeriodStart) }} - {{ formatDate(stub.payPeriodEnd) }}</p>
              <p v-if="stub.propertyName">Property: {{ stub.propertyName }}</p>
              <p v-if="stub.memo">Memo: {{ stub.memo }}</p>
            </div>
          </div>
        </div>

        <div class="section-block">
          <h3 class="section-label">EARNINGS</h3>
          <table class="blue-table">
            <thead>
              <tr>
                <th class="desc-col">DESCRIPTION</th>
                <th class="right">RATE</th>
                <th class="right">HOURS</th>
                <th class="right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Regular Pay</td>
                <td class="right">{{ currency(stub.hourlyRate) }}</td>
                <td class="right">{{ formattedNum(stub.totalHours) }}</td>
                <td class="right">{{ currency(regularPayAmount) }}</td>
              </tr>
              <tr v-if="stub.overtimeHours > 0">
                <td>Overtime Pay</td>
                <td class="right">{{ currency(stub.overtimeRate) }}</td>
                <td class="right">{{ formattedNum(stub.overtimeHours) }}</td>
                <td class="right">{{ currency(overtimePayAmount) }}</td>
              </tr>
              <tr v-if="stub.refund > 0">
                <td>Refund / Reimbursement</td>
                <td class="right">—</td>
                <td class="right">—</td>
                <td class="right">{{ currency(stub.refund) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" class="right total-label">Total Earnings</td>
                <td class="right total-val">{{ currency(totalEarnings) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="split-row">
          <div class="split-col">
            <h3 class="section-label">DEDUCTIONS</h3>
            <table class="blue-table">
              <thead>
                <tr>
                  <th>DESCRIPTION</th>
                  <th class="right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="stub.federalIncome > 0">
                  <td>Federal Income Tax</td>
                  <td class="right">{{ currency(stub.federalIncome) }}</td>
                </tr>
                <tr v-if="stub.socialSecurity > 0">
                  <td>Social Security</td>
                  <td class="right">{{ currency(stub.socialSecurity) }}</td>
                </tr>
                <tr v-if="stub.medicare > 0">
                  <td>Medicare</td>
                  <td class="right">{{ currency(stub.medicare) }}</td>
                </tr>
                <tr v-if="stub.rent > 0">
                  <td>Rent</td>
                  <td class="right">{{ currency(stub.rent) }}</td>
                </tr>
                <tr v-if="stub.otherDeductions > 0">
                  <td>Other Deductions</td>
                  <td class="right">{{ currency(stub.otherDeductions) }}</td>
                </tr>
                <tr v-if="totalDeductions === 0">
                  <td colspan="2" class="empty-cell">No Deductions</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="right total-label">Total Deductions</td>
                  <td class="right total-val">{{ currency(totalDeductions) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="split-col">
            <h3 class="section-label">YEAR TO DATE (YTD)</h3>
            <table class="blue-table">
              <thead>
                <tr>
                  <th>DESCRIPTION</th>
                  <th class="right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Gross Wages</td>
                  <td class="right">{{ currency(stub.ytdGrossWages) }}</td>
                </tr>
                <tr>
                  <td>Federal Income Tax</td>
                  <td class="right">{{ currency(stub.ytdFederalIncome) }}</td>
                </tr>
                <tr>
                  <td>Social Security</td>
                  <td class="right">{{ currency(stub.ytdSocialSecurity) }}</td>
                </tr>
                <tr>
                  <td>Medicare</td>
                  <td class="right">{{ currency(stub.ytdMedicare) }}</td>
                </tr>
                <tr>
                  <td>Rent</td>
                  <td class="right">{{ currency(stub.ytdRent) }}</td>
                </tr>
                <tr>
                  <td>Net Wages</td>
                  <td class="right">{{ currency(stub.ytdNetWages) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="net-pay-wrapper">
          <div class="net-pay-box">
            <div class="np-label">NET PAY</div>
            <div class="np-amount">{{ currency(stub.netWages) }}</div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { db, auth } from '@/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import html2pdf from 'html2pdf.js'
import Swal from 'sweetalert2'
import { toLocalDate } from '@/utils/dates'

const route = useRoute()
const stub = ref(null)
const employeeData = ref(null)
const invoiceData = ref(null)
const employeeAddress = ref('')
const loading = ref(true)
const error = ref(null)

const formatDate = (val) => {
  if (!val) return '—'
  const date = toLocalDate(val)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }).format(date)
}

const formatDateRange = (start, end) => {
  if (!start || !end) return '—'
  return `${formatDate(start)} - ${formatDate(end)}`
}

const formattedNum = (val) => Number(val || 0).toFixed(2)

const currency = (val) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(val || 0)
}

const regularPayAmount = computed(() => {
  if (!stub.value) return 0
  return (stub.value.hourlyRate || 0) * (stub.value.totalHours || 0)
})

const overtimePayAmount = computed(() => {
  if (!stub.value) return 0
  return (stub.value.overtimeRate || 0) * (stub.value.overtimeHours || 0)
})

const totalEarnings = computed(() => {
  if (!stub.value) return 0
  return (stub.value.grossWages || 0) + (stub.value.refund || 0)
})

const totalDeductions = computed(() => {
  if (!stub.value) return 0
  return (stub.value.federalIncome || 0) +
    (stub.value.socialSecurity || 0) +
    (stub.value.medicare || 0) +
    (stub.value.rent || 0) +
    (stub.value.otherDeductions || 0)
})

// Pay stub layout shared by Download PDF and Print, so both always match
function buildStubHtml() {
  const payStub = stub.value
  const employee = employeeData.value
  const companyName = invoiceData.value?.companyName || 'RTH GROUP INC.'
  const companyAddress = invoiceData.value?.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'

  return `
    <div style="padding: 12px; font-family: Arial, sans-serif; color: #333; font-size: 13px;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #2563eb;">
        <div>
          <h1 style="margin: 0; color: #1f2937; font-size: 18px; font-weight: bold;">${companyName}</h1>
          <p style="margin: 2px 0; color: #666; font-size: 11px;">${companyAddress}</p>
          <p style="margin: 2px 0; color: #666; font-size: 11px;">Phone: (865) 352-8700</p>
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; color: #2563eb; font-size: 20px; font-weight: bold;">PAY STUB</h2>
          <p style="margin: 2px 0; font-size: 11px;">Pay Date: ${formatDate(payStub.payDate)}</p>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 8px;">
        <div style="background-color: #f8f9fa; padding: 8px; border-radius: 4px;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold;">EMPLOYEE</h3>
          <p style="margin: 2px 0; font-weight: bold; font-size: 12px;">${payStub.employeeName}</p>
          <p style="margin: 2px 0; font-size: 11px;">ID: ${payStub.employeeNumber}</p>
          ${employee?.usAddress ? `
            <p style="margin: 6px 0 0 0; padding-top: 6px; border-top: 1px solid #ddd; font-size: 10px; line-height: 1.2;">
              <strong>Address:</strong> ${employee.usAddress}, ${employee.usCity}, ${employee.usState} ${employee.usZipCode}, ${employee.usCountry}
            </p>
          ` : ''}
        </div>
        <div style="background-color: #f8f9fa; padding: 8px; border-radius: 4px;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold;">PAY PERIOD</h3>
          <p style="margin: 2px 0; font-size: 11px;">${formatDateRange(payStub.payPeriodStart, payStub.payPeriodEnd)}</p>
          <p style="margin: 4px 0 0 0; font-size: 11px;"><strong>Property:</strong> ${payStub.propertyName}</p>
          ${payStub.memo ? `
            <p style="margin: 6px 0 0 0; padding-top: 6px; border-top: 1px solid #ddd; font-size: 10px;"><strong>Memo:</strong> ${payStub.memo}</p>
          ` : ''}
        </div>
      </div>

      <div style="margin-bottom: 6px;">
        <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">EARNINGS</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #2563eb; color: white;">
              <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">RATE</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">HOURS</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Regular Pay</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.hourlyRate)}</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${Number(payStub.totalHours).toFixed(2)}</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency((payStub.hourlyRate || 0) * (payStub.totalHours || 0))}</td>
            </tr>
            ${payStub.overtimeHours > 0 ? `
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Overtime Pay</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.overtimeRate)}</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${Number(payStub.overtimeHours).toFixed(2)}</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency((payStub.overtimeRate || 0) * (payStub.overtimeHours || 0))}</td>
              </tr>
            ` : ''}
            ${payStub.refund > 0 ? `
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Refund / Reimbursement</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">-</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">-</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.refund)}</td>
              </tr>
            ` : ''}
          </tbody>
          <tfoot>
            <tr style="background-color: #f8f9fa; font-weight: bold;">
              <td colspan="3" style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">Total Earnings</td>
              <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">${currency(payStub.grossWages + (payStub.refund || 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style="display: flex; gap: 10px; margin-bottom: 6px;">
        <div style="flex: 1;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">DEDUCTIONS</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #2563eb; color: white;">
                <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
                <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${payStub.federalIncome > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Federal Income Tax</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.federalIncome)}</td>
                </tr>
              ` : ''}
              ${payStub.socialSecurity > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Social Security</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.socialSecurity)}</td>
                </tr>
              ` : ''}
              ${payStub.medicare > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Medicare</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.medicare)}</td>
                </tr>
              ` : ''}
              ${payStub.rent > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Rent</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.rent)}</td>
                </tr>
              ` : ''}
              ${payStub.otherDeductions > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Other Deductions</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.otherDeductions)}</td>
                </tr>
              ` : ''}
            </tbody>
            <tfoot>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">Total Deductions</td>
                <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">${currency(totalDeductions.value)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style="flex: 1;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">YEAR TO DATE (YTD)</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #2563eb; color: white;">
                <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
                <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Gross Wages</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.ytdGrossWages || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Federal Income Tax</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.ytdFederalIncome || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Social Security</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.ytdSocialSecurity || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Medicare</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.ytdMedicare || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Rent</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.ytdRent || 0)}</td>
              </tr>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td style="padding: 3px; border: 1px solid #ddd; font-size: 11px;">Net Wages</td>
                <td style="padding: 3px; border: 1px solid #ddd; text-align: right; font-size: 11px;">${currency(payStub.ytdNetWages || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; margin-top: 6px;">
        <div style="border: 2px solid #000; padding: 6px 16px; text-align: center; background: #f9f9f9;">
          <h3 style="margin: 0 0 3px 0; text-transform: uppercase; font-size: 11px; color: #1f2937; font-weight: bold;">NET PAY</h3>
          <div style="font-size: 19px; font-weight: bold; color: #1f2937;">${currency(payStub.netWages)}</div>
        </div>
      </div>
    </div>
  `

}

// e.g. PayStub_EMP26075112_2026-10-19.pdf
function pdfFileName() {
  const payDate = typeof stub.value.payDate === 'string' ? stub.value.payDate : ''
  return `PayStub_${stub.value.employeeNumber || 'employee'}_${payDate || stub.value.id}.pdf`
}

const downloading = ref(false)

async function downloadPdf() {
  if (!stub.value || downloading.value) return
  downloading.value = true
  const element = document.createElement('div')
  element.innerHTML = buildStubHtml()
  try {
    await html2pdf().set({
      margin: 10,
      filename: pdfFileName(),
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    }).from(element).save()
  } catch (err) {
    console.error('PDF generation failed:', err)
    Swal.fire({ icon: 'error', title: 'Download failed', text: 'The PDF could not be created. Please try again, or use Print instead.' })
  } finally {
    downloading.value = false
  }
}

// Print the same layout through a hidden frame (opens the print / share sheet on phones)
function printStub() {
  if (!stub.value) return
  const frame = document.createElement('iframe')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(frame)
  const frameDoc = frame.contentWindow.document
  frameDoc.open()
  frameDoc.write(`<!doctype html><html><head><title>${pdfFileName().replace(/.pdf$/, '')}</title>`
    + '<style>@page { margin: 12mm; } body { margin: 0; }</style></head><body>'
    + buildStubHtml() + '</body></html>')
  frameDoc.close()
  frame.contentWindow.focus()
  setTimeout(() => {
    frame.contentWindow.print()
    setTimeout(() => frame.remove(), 1000)
  }, 250)
}

onMounted(() => {
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      error.value = 'Unauthenticated'
      loading.value = false
      return
    }
    const stubId = route.params.id
    if (!stubId) {
      error.value = 'Invalid Pay Stub ID'
      loading.value = false
      return
    }
    try {
      // 1. Fetch Pay Stub
      const docRef = doc(db, 'payStubs', stubId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        stub.value = { id: docSnap.id, ...data }

        // 2. Fetch Employee Details (for Address)
        if (data.employeeId) {
          try {
            const empDoc = await getDoc(doc(db, 'employees', data.employeeId))
            if (empDoc.exists()) {
              employeeData.value = { id: empDoc.id, ...empDoc.data() }
              // Construct display address
              const emp = employeeData.value
              const parts = []
              if (emp.usAddress) parts.push(emp.usAddress)
              else if (emp.address) parts.push(emp.address) // Fallback

              // Build City/State line
              let cityState = ''
              if (emp.usCity) cityState += emp.usCity
              else if (emp.city) cityState += emp.city

              if (emp.usState) cityState += (cityState ? ', ' : '') + emp.usState
              else if (emp.state) cityState += (cityState ? ', ' : '') + emp.state

              if (emp.usZipCode) cityState += ' ' + emp.usZipCode
              else if (emp.zipCode) cityState += ' ' + emp.zipCode

              if (cityState) parts.push(cityState)

              if (emp.usCountry) parts.push(emp.usCountry)

              if (parts.length > 0) {
                employeeAddress.value = parts.join(', ')
              }
            }
          } catch (e) {
            console.warn('Could not fetch employee details', e)
          }
        }

        // 3. Fetch Invoice Details (for Company Info)
        if (data.invoiceId) {
          try {
            const invDoc = await getDoc(doc(db, 'invoices', data.invoiceId))
            if (invDoc.exists()) {
              invoiceData.value = { id: invDoc.id, ...invDoc.data() }
            }
          } catch (e) {
            console.warn('Could not fetch invoice details', e)
          }
        }
      } else {
        error.value = 'Pay stub not found.'
      }
    } catch (err) {
      console.error(err)
      error.value = 'Error loading pay stub.'
    } finally {
      loading.value = false
    }

    // "PDF" button on the My Pay Stubs list opens this page with ?download=1
    if (stub.value && route.query.download === '1') downloadPdf()
  })
})
</script>

<style scoped>
/* Page Layout */
.details-container {
  min-height: 100vh;
  background-color: #e5e7eb;
  padding: 2rem;
  font-family: Arial, sans-serif;
  color: #333;
}

.loader {
  border: 4px solid #ced4da;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-state,
.error-state {
  text-align: center;
  margin-top: 4rem;
}

.stub-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

/* Toolbar */
.stub-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.print-btn {
  background: #2563eb;
  color: white;
  border: 1px solid #2563eb;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.print-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.print-btn--secondary {
  background: #fff;
  color: #2563eb;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.print-btn:hover {
  background: #1d4ed8;
}

.back-btn {
  text-decoration: none;
  color: #4b5563;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
}

/* Paper Sheet - On-page view */
.paper-sheet {
  background: white;
  padding: 40px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Header */
.print-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  border-bottom: 3px solid #3b82f6;
  padding-bottom: 10px;
}

.company-info h1 {
  font-size: 20px;
  margin: 0 0 5px 0;
  font-weight: bold;
}

.company-info p {
  margin: 2px 0;
  font-size: 13px;
  color: #555;
}

.pay-stub-title {
  text-align: right;
}

.pay-stub-title h2 {
  color: #3b82f6;
  font-size: 24px;
  margin: 0;
}

.pay-stub-title p {
  font-size: 14px;
  font-weight: bold;
  margin: 5px 0 0 0;
}

/* Info Row */
.info-row {
  display: flex;
  background: #f8f9fa;
  padding: 15px;
  margin-bottom: 20px;
  gap: 40px;
}

.info-col {
  flex: 1;
}

.section-label {
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
  font-weight: 800;
  margin: 0 0 8px 0;
}

.info-content p {
  margin: 2px 0;
  font-size: 13px;
  font-weight: 600;
}

.emp-name {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 4px !important;
}

/* Tables */
.section-block {
  margin-bottom: 25px;
}

.blue-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.blue-table thead th {
  background-color: #3b82f6;
  color: white;
  text-align: left;
  padding: 6px 8px;
  text-transform: uppercase;
  font-weight: bold;
}

.blue-table td {
  border-bottom: 1px solid #ddd;
  padding: 6px 8px;
}

.blue-table .right {
  text-align: right;
}

.blue-table tfoot td {
  border-top: 2px solid #333;
  font-weight: bold;
  padding-top: 8px;
}

.total-label {
  text-align: right;
  padding-right: 18px;
}

/* Split Row */
.split-row {
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
}

.split-col {
  flex: 1;
}

/* Net Pay */
.net-pay-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.net-pay-box {
  border: 2px solid #000;
  padding: 10px 20px;
  text-align: center;
  min-width: 150px;
}

.np-label {
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.np-amount {
  font-size: 22px;
  font-weight: bold;
}
</style>
